import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { styles } from "./styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  withStyles,
} from "@material-ui/core";
import {
  getDataAugmentation,
  updateAssociations,
  getAssociations,
} from "../../../../../../../API/Entity";
import ModalDialogContext from "../../../../../../../Context/ModalDialog/ModalDialogContext";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { CreateAssociationDialog } from "../../../../../../EntityAssociations/Components";
import { recordTypes } from "../../../../../../../API/config";
import {
  NoAccessScreen,
  LoadingIndicator,
  Pagination,
} from "../../../../../../../Component";
import AssociationDetail from "./AssociationDetail";

const AssociationsTable = (props) => {
  const {
    entity,
    sources,
    availableSources,
    reloadParent,
    user,
    hasPermission,
    hasRecordPermission,
    classes,
  } = props;

  const { showMessage, showErrorMessage, showConfirmation } =
    useContext(ModalDialogContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState({ main: false });
  const [augmentableAssociations, setAugmentableAssociations] = useState({});
  const [selectedRecord, setSelectedRecord] = useState();
  const [associationSearchQuery, setAssociationSearchQuery] = useState();
  const [associationType, setAssociationType] = useState();
  const [openAddAssociationDialog, setOpenAddAssociationDialog] =
    useState(false);
  const [associations, setAssociations] = useState([]);
  const [allowedAssociations, setAllowedAssociations] = useState([]);
  const [panelExpanded, setPanelExpanded] = useState("0");
  const [page, setPage] = useState({});
  const [pagination, setPagination] = useState([]);
  const [paginationSources, setPaginationSources] = useState([]);
  const [sourceNext, setSourceNext] = useState({});
  const [hasAccess, setHasAccess] = useState(false);
  const [sourceNames, setSourceNames] = useState([]);

  const loadNext = (pageSources, pg) => {
    setPaginationSources(pageSources);
    if (pg) {
      setPage(pg);
    } else {
      pageSources.forEach((source) => {
        if (!page[source]) {
          const sourceCategory = source
            .split("(")[0]
            .split(" ")
            .join("")
            .toLowerCase();
          for (let p of Object.keys(page)) {
            if (p.toLowerCase() === sourceCategory) {
              setPage((oldVal) => ({
                ...oldVal,
                [source]: page[p] + 1,
              }));
              break;
            } else {
              setPage((oldVal) => ({
                ...oldVal,
                [source]: 1,
              }));
            }
          }
        } else {
          setPage((oldVal) => ({
            ...oldVal,
            [source]: page[source] ? page[source] + 1 : 1,
          }));
        }
      });
    }
  };

  useEffect(() => {
    const resetStates = () => {
      setAugmentableAssociations({});
      setSelectedRecord();
      setAssociations([]);
      setPanelExpanded("0");
      setPage({});
      setPagination([]);
      setPaginationSources([]);
      setSourceNames([]);
      setSourceNext({});
      setAssociationSearchQuery();
      setAssociationType();
    };

    const fetchSources = () => {
      let srcs =
        Object.keys(augmentableAssociations).length > 0
          ? Object.keys(augmentableAssociations).map((s) => s)
          : availableSources
              .filter((s) =>
                sources.map(({ label }) => label).includes(s.label),
              )
              .map((s) => s.value);
      let pages = {};
      srcs.forEach((src) => {
        pages[src] = 1;
      });
      loadNext(srcs, pages);
    };

    const loadAssociations = async () => {
      setLoading({ main: true });
      try {
        const result = await getAssociations(
          entity.id,
          entity.entity_category,
          entity.entity_subcategory,
        );
        const { data } = result || {};
        const { associations = [], allowedAssociations = [] } = data || {};
        setAssociations(associations);
        setAllowedAssociations(allowedAssociations);
      } catch (error) {
        showErrorMessage({ error });
      } finally {
        setLoading({ main: false });
        fetchSources();
      }
    };

    const setPermissions = async () => {
      const { permissions = {} } = entity;

      const canUpdateAssociations = hasRecordPermission(
        permissions,
        recordTypes.record.actions.updateAssociation.name,
      );
      setHasAccess(canUpdateAssociations);

      if (canUpdateAssociations) {
        await loadAssociations();
      }
    };

    if (sources) {
      resetStates();
      setPermissions();
    }
  }, [entity, sources]);

  useEffect(() => {
    const setPageInfo = () => {
      let pageSources = [];
      paginationSources.forEach((source) => {
        pageSources.push({ source: source, pageNumber: page[source] });
      });
      setPagination(pageSources);
    };

    if (paginationSources.length > 0) {
      let noEmptyPages = false;
      paginationSources.forEach((source) => {
        if (page[source] && page[source] > 0) {
          noEmptyPages |= true;
        } else {
          noEmptyPages &= false;
        }
      });

      if (noEmptyPages) {
        setPageInfo();
      }
    }
  }, [page]);

  useEffect(() => {
    const setAugmentationData = (augmentationData) => {
      const augmentAssociations = {};
      Object.keys(augmentationData).forEach((sourceName) => {
        const { associations = {} } = augmentationData[sourceName];
        if (augmentableAssociations[sourceName]) {
          augmentAssociations[sourceName] = [
            ...augmentableAssociations[sourceName],
            ...associations.data,
          ];
        } else {
          augmentAssociations[sourceName] = associations.data;
        }

        setSourceNext((oldVal) => ({
          ...oldVal,
          [sourceName]: associations.hasNext,
        }));

        setSourceNames((oldVal) => [...new Set([...oldVal, sourceName])]);
      });

      Object.keys(augmentAssociations).forEach((src) => {
        setAugmentableAssociations((oldVal) => ({
          ...oldVal,
          [src]: augmentAssociations[src],
        }));
      });
    };

    const loadAvailableAssociations = async () => {
      paginationSources.forEach((source) => {
        setLoading((oldVal) => ({
          ...oldVal,
          [source]: true,
        }));
      });
      try {
        const response = await getDataAugmentation(
          entity.id,
          availableSources
            .filter((s) => sources.map(({ label }) => label).includes(s.label))
            .map((s) => s.value),
          "associations",
          pagination,
        );
        const { data = {} } = response;
        const { sourcesData = {}, errors } = data || {};
        if (errors && errors.length > 0) {
          throw new Error(errors.join("\n"));
        }
        setAugmentationData(sourcesData);
      } catch (error) {
        showErrorMessage({ error });
      }
      paginationSources.forEach((source) => {
        setLoading((oldVal) => ({
          ...oldVal,
          [source]: false,
        }));
      });

      setLoadingSources();
    };

    const setLoadingSources = () => {
      Object.keys(augmentableAssociations).forEach((source) => {
        setLoading((oldVal) => ({
          ...oldVal,
          [source]: false,
        }));
      });
    };

    if (pagination.length > 0) {
      loadAvailableAssociations();
    }
  }, [pagination]);

  const addAssociation = (association) => {
    const { additionalInfo } = association;
    let selectRecord = {
      recordName: association.name,
      selectedCategory: association.legalStatus,
    };
    if (additionalInfo) {
      const { address, documents } = additionalInfo;
      if (address) {
        selectRecord.address = address;
      }
      if (documents) {
        selectRecord.documents = documents;
      }
    }
    setSelectedRecord(selectRecord);
    setAssociationSearchQuery(association.name);
    setAssociationType(association.associatedAs);
    setOpenAddAssociationDialog(true);
  };

  const createAssociation = async (newAssociations) => {
    try {
      const {
        targetEntityId,
        targetMdmId,
        targetDnbId,
        associations,
        additionalInformation,
      } = newAssociations;

      await updateAssociations({
        sourceEntityId: entity.id,
        targetEntityId,
        targetMdmId,
        targetDnbId,
        associations: associations,
        additionalInformation,
        action: recordTypes.record.actions.augmentData.name,
      });
      closeAddAssociationDialog();
      reloadParent(true);
      showMessage({
        message: t("messages.record.associations.associationsUpdated"),
      });
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  const closeAddAssociationDialog = () => {
    setOpenAddAssociationDialog(false);
  };

  const renderAssociationTableHeader = (source) => {
    return (
      <TableHead className={classes.tableHeader}>
        <TableRow>
          <TableCell
            className={classes.columnHeader}
            scope="col"
            align="center"
            width={250}
          >
            Name
          </TableCell>
          <TableCell
            className={classes.columnHeader}
            scope="col"
            align="center"
            width={250}
            style={{ whiteSpace: "nowrap" }}
          >
            Source Association
          </TableCell>
          <TableCell
            className={classes.columnHeader}
            scope="col"
            align="center"
            width={250}
            style={{ whiteSpace: "nowrap" }}
          >
            Interpreted Association
          </TableCell>
          <TableCell
            className={classes.columnHeader}
            scope="col"
            align="center"
          >
            Action
          </TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderAssociationTableBody = (source) => (
    <TableBody>
      {augmentableAssociations[source] &&
        augmentableAssociations[source].map((assoc, idx) => {
          return (
            <AssociationDetail
              key={idx}
              association={assoc}
              handleAdd={addAssociation}
            />
          );
        })}
    </TableBody>
  );

  const handlePanelToggle = (panel) => (event, newExpanded) => {
    setPanelExpanded(newExpanded ? panel : false);
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "30vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const renderButtonLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "20px",
        }}
      >
        <LoadingIndicator size={20} />
      </div>
    );
  };

  const renderAssociationTable = () => {
    return (
      <>
        {openAddAssociationDialog && (
          <CreateAssociationDialog
            isDialogOpen={openAddAssociationDialog}
            existingAssociations={associations}
            allowedAssociations={allowedAssociations}
            selectedRecord={selectedRecord}
            user={user}
            onSubmit={createAssociation}
            searchQuery={associationSearchQuery}
            defaultType={associationType}
            onClose={closeAddAssociationDialog}
            hasPermission={hasPermission}
          />
        )}
        {hasAccess ? (
          loading["main"] ? (
            renderLoadingIndicator()
          ) : (
            <>
              <Grid container spacing={1} style={{ overflowX: "auto" }}>
                {sourceNames.map((source, idx) => (
                  <Grid item key={idx}>
                    <Accordion
                      expanded={panelExpanded === `${idx}`}
                      onChange={handlePanelToggle(`${idx}`)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${source
                          .split(" ")
                          .join("")
                          .toLowerCase()}-panel-content`}
                        id={`${source
                          .split(" ")
                          .join("")
                          .toLowerCase()}-panel-header`}
                      >
                        <Typography>{source}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container>
                          <Grid item xs={12}>
                            <Table
                              className={classes.table}
                              size="small"
                              key={idx}
                            >
                              {renderAssociationTableHeader(source)}
                              {renderAssociationTableBody(source)}
                            </Table>
                          </Grid>
                          <Grid item xs={12}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                marginTop: 16,
                              }}
                            >
                              <Button
                                variant="outlined"
                                onClick={(_) => loadNext([source])}
                                disabled={
                                  loading[source] || !sourceNext[source]
                                }
                              >
                                {loading[source]
                                  ? renderButtonLoadingIndicator()
                                  : t("labels.common.pagination.loadMore")}
                              </Button>
                            </div>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                ))}
              </Grid>
            </>
          )
        ) : (
          <div style={{ position: "relative" }}>
            <NoAccessScreen />
          </div>
        )}
      </>
    );
  };
  return renderAssociationTable();
};

export default withStyles(styles, { withTheme: true })(AssociationsTable);
